/**
 * Reubicación de productos detrás de "espacios vacíos fijos" en estantes normales.
 *
 * Módulo sin imports a propósito (igual que `spaces_product_compression.ts`): es lógica pura
 * y así queda testeable sin arrastrar el grafo de módulos del editor.
 */

export interface FixedSpaceRange {
  /** Borde izquierdo del espacio fijo. */
  start: number;
  /** Borde derecho del espacio fijo. */
  end: number;
}

/**
 * Corre `x` hacia la derecha hasta que un producto de ancho `width` deje de solapar
 * cualquier espacio fijo. Devuelve la `x` resuelta.
 *
 * GOTCHA (planograma 1190 / axion): `sepX` puede llegar NEGATIVO cuando el estante está
 * pasado de ancho y tiene `__shelfAvoidCheckSpace` (`noValidateSize` del backend) — ver
 * `_recalcPosAndSize_normalShelf`. Con `sepX < 0`, saltar a `fs.end + sepX` deja la `x`
 * DENTRO del espacio fijo, el solape se vuelve a detectar y se reasigna el mismo valor:
 * el bucle no avanza nunca y cuelga la pestaña (el layout corre sincrónico en el render).
 *
 * Terminación garantizada por dos vías independientes:
 *  1. `jumpSepX` nunca es negativo ⇒ cada salto mueve `rv` estrictamente a la derecha, y un
 *     espacio fijo ya superado no puede volver a solapar (solapar exige `rv < fs.end`).
 *  2. Por lo anterior cada espacio fijo provoca a lo sumo un salto, así que `N + 1` pasadas
 *     alcanzan siempre (`N` saltos + la pasada que confirma que no hay solape). El tope se
 *     calcula sobre `N` en vez de ser una constante mágica: así no se rinde en silencio,
 *     dejando un solape, en un estante con muchos espacios fijos.
 */
export const resolveXAfterFixedSpaces = (x: number, width: number, fixedSpaces: FixedSpaceRange[], sepX = 0, minSepX = 0): number => {
  const ranges = fixedSpaces || [];
  if (!ranges.length) return x;

  // Nunca saltar con separación negativa: es lo que rompía la convergencia.
  const jumpSepX = Math.max(sepX, minSepX, 0);
  const maxIterations = ranges.length + 1;

  let rv = x;
  let resolved = false;
  let iterations = 0;

  while (!resolved && iterations < maxIterations) {
    resolved = true;
    iterations++;

    for (const fs of ranges) {
      if (rv < fs.end && rv + width > fs.start) {
        rv = fs.end + jumpSepX;
        resolved = false;
        break;
      }
    }
  }

  return rv;
};
